import React, { useState, useEffect, useRef } from "react";

import GLOBAL from "../../global";
import examService from "../../services/ExamService";
import AlertModal from "./AlertModal";

import { playAudio, getMarkup } from "../../utils/Utils";
import { useTenantFeatures } from "../../features/tenantFeatures/useTenantFeature";


const redDotStyle = {
  minWidth: "13.05px",
  minHeight: "12.17px",
  backgroundColor: "red",
  borderRadius: "50%",
  marginRight: "2rem",
};

const TakeBiometricMovements = (props) => {
  const { onClickMovementCell, data, setTakeBiometricMovementModal } = props;
  const { TestId, BodyRegionList } = data;
  const [answers, setAnswers] = useState({});
  const [clickSubmit, setClickSubmit] = useState(false);
  const [countryIndicationConfirmation, setCountryIndicationConfirmation] =
    useState(false);
  const [dynamicQuestions, setDynamicQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [doesPatientAnsweredYes, setDoesPatientAnsweredYes] = useState(false);

  const lastCollectedAnswer = useRef(null);

  const { isCautionQuestionEnable } = useTenantFeatures();

  useEffect(() => {
    if (TestId) {
      fetchCautionQuestions();
    }
  }, [TestId]);

  const fetchCautionQuestions = async () => {
    if (!isCautionQuestionEnable) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const response = await examService.getBiometricByExamId({ TestId: TestId });
    if (response.success && response.data) {
      const examResponse = response.data?.Message?.ExamResponse || response.data?.ExamResponse || response.data?.data?.ExamResponse || [];
      
      // Include all non-hidden questions from the API
      // const filteredQuestions = examResponse.filter(q => !q.IsDefaultHide);
      const filteredQuestions = examResponse;

        const fetchedQuestions = filteredQuestions.map((q) => {
        const mappedQ = {
          questionId: q.QuestionId,
          text: q.QuestionTitle ? q.QuestionTitle.replace(/^\*\s*/, "") : "",
          placeholder: q.Placeholder,
          isCaution: q.QuestionTitle && q.QuestionTitle.toLowerCase().includes("caution"),
          // voiceUrl: q.VoiceUrl,
          options: (q.Responses || []).map((r) => ({
            answerId: r.AnswerId,
            text: r.AnswerTitle,
            value: (r.AnswerTitle || "").toLowerCase(),
          })),
        };
        return mappedQ;
      });

      setDynamicQuestions(fetchedQuestions);

      // Initialize answers from API response
      const initialAnswers = {};
      fetchedQuestions.forEach((q) => {
        // Find if already answered in the API response (e.g. from Message.ExamResponse items)
        const originalQ = examResponse.find(orig => orig.QuestionId === q.questionId);
        initialAnswers[q.questionId] = originalQ?.SingleSelectAnswerId || -1;
      });
      setAnswers(initialAnswers);
      
      // Check if any existing answer is "Yes"
      checkDoesPatientAnsweredYes(initialAnswers, fetchedQuestions);

      // Automatically proceed if all questions are already answered as "No" or if no questions are found
      const shouldProceed = fetchedQuestions.length === 0 || fetchedQuestions.every(q => {
        const answerId = initialAnswers[q.questionId];
        return answerId !== -1 && !isYesAnswer(q.questionId, answerId, fetchedQuestions);
      });

      if (shouldProceed) {
        if (onClickMovementCell) {
          const result = onClickMovementCell();
          if (typeof result === 'function') {
            result();
          }
        }
        setTakeBiometricMovementModal && setTakeBiometricMovementModal(false);
        return;
      }
    }
    setLoading(false);
  };

  const onSubmitDocument = async (e) => {
    e.preventDefault();
    setClickSubmit(true);
    const finalAnswers = [];
    let flag = false;
    for (const key in answers) {
      if (isYesAnswer(parseInt(key), answers[key]) || answers[key] === -1) {
        flag = true;
        return;
      }
      finalAnswers.push({ QuestionId: parseInt(key), AnswerId: answers[key] });
    }

    setIsOpening(true);

    const payload = {
      BodyRegionList: BodyRegionList,
      TestId: TestId,
      Answers: finalAnswers,
      // Answers: answers
    };

    const responseAssessment = await examService.saveAssessments(payload);
    const { success } = responseAssessment;

    if (!success) {
      return responseAssessment;
    }

    setIsOpening(false);

    if (!flag) {
      if (onClickMovementCell) {
        const result = onClickMovementCell();
        // Handle both curried functions (old) and regular functions (new chatbot)
        if (typeof result === 'function') {
          result();
        }
      }
      // Close caution modal when all answers are No
      setTakeBiometricMovementModal && setTakeBiometricMovementModal(false);
    } else {
      setTakeBiometricMovementModal && setTakeBiometricMovementModal(false);
    }
  };

  const onConfirmCountryIndication = () => {
    const answersCopy = {
      ...answers,
      [lastCollectedAnswer.current.questionId]:
        lastCollectedAnswer.current.answerId,
    };
    setAnswers(answersCopy);
    checkDoesPatientAnsweredYes(answersCopy);
    GLOBAL.COUNTRY_INDICATION_CHAT = undefined; ////TODO: While add Redux here will remove this
    GLOBAL.COUNTRY_INDICATION_CHAT_RESPONSE = undefined;
    setCountryIndicationConfirmation(false);
  };

  const countryIndicationClose = () => {
    setCountryIndicationConfirmation(false);
    GLOBAL.COUNTRY_INDICATION_CHAT = undefined; ////TODO: While add Redux here will remove this
    GLOBAL.COUNTRY_INDICATION_CHAT_RESPONSE = undefined;
  };

  const isYesAnswer = (questionId, answerId, questions = dynamicQuestions) => {
    const question = questions.find((q) => q.questionId === questionId);
    if (question) {
      const option = question.options.find((opt) => opt.answerId === answerId);
      return option && option.text.toLowerCase() === "yes";
    }
    return false;
  };

  const collectAnswer = (e, questionId, answerId) => {
    e.preventDefault();
    lastCollectedAnswer.current = {
      questionId: questionId,
      answerId: answerId,
    };
    
    if (isYesAnswer(questionId, answerId)) {
      setCountryIndicationConfirmation(true);
      return;
    }
    const answersCopy = { ...answers, [questionId]: answerId };
    checkDoesPatientAnsweredYes(answersCopy);
    setAnswers(answersCopy);
  };

  const checkDoesPatientAnsweredYes = (currentAnswers = [], questions = dynamicQuestions) => {
    let hasYes = false;
    for (const key in currentAnswers) {
      if (isYesAnswer(parseInt(key), currentAnswers[key], questions)) {
        hasYes = true;
        break;
      }
    }
    setDoesPatientAnsweredYes(hasYes);
  };

  return (
    <>
      <form className="kt-form d-flex-center-center flex-direction-column">
        <div className="kt-portlet__body take-biometric-movement-container" style={{ minHeight: '150px' }}>
          {loading ? (
            <div className="d-flex-center-center padding-2rem flex-direction-column">
              <i className="fa-solid fa-spinner fa-spin color-dashboard margin-bottom-1-rem" style={{ fontSize: '2rem' }}></i>
              <div className="mmh-font-xl">Loading questions...</div>
            </div>
          ) : (
            dynamicQuestions.map((question) => (
              <div
                key={question.questionId}
                className="form-group validated"
              >
                <div className="display-flex align-items-start">
                  <i
                    className="color-red fa-solid fa-triangle-exclamation padding-right-0-5-rem"
                    style={{ marginTop: "5px", fontSize: '1.2rem' }}
                  ></i>

                  <div className={"margin-bottom-1-rem width-100"}>
                    <div className="display-flex align-items-center justify-content-between">
                      <label
                        className="take-biometric-movement-question-title"
                        htmlFor={`question-${question.questionId}`}
                      >
                        {question.text}
                      </label>
                      {/* {question.voiceUrl && (
                        <i
                          className="fa-solid fa-volume-high pointer color-dashboard margin-left-1rem"
                          onClick={() => playAudio(question.voiceUrl)}
                          title="Play Audio"
                        ></i>
                      )} */}
                    </div>
                    <div className="kt-radio-inline margin-top-10-px">
                      {question.options.map((option) => (
                        <button
                          key={option.answerId}
                          type="button"
                          className={`btn pointer button-row btn-border margin-right-0-75rem ${answers[question.questionId] === option.answerId ? "button button-dashboard" : ""}`}
                          onClick={(e) =>
                            collectAnswer(e, question.questionId, option.answerId)
                          }
                        >
                          {option.text}
                        </button>
                      ))}
                    </div>
                    {clickSubmit && answers[question.questionId] === -1 && (
                      <div className="invalid-feedback color-red">
                        This field is required.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {doesPatientAnsweredYes && !loading && (
          <div className="alert alert-danger margin-bottom-1-rem width-90" role="alert">
            <div className="alert-text">
              You should not proceed with this test. Please contact your administrator.
            </div>
          </div>
        )}

        <div className="d-flex-center-center margin-bottom-1-rem">
          <button
            onClick={onSubmitDocument}
            type="submit"
            className="button button-dashboard padding-12px"
            disabled={loading || isOpening || doesPatientAnsweredYes}
          >
            {isOpening ? "Saving..." : "Submit"}
          </button>
        </div>
      </form>

      <AlertModal
        confirmBtnText="Yes"
        confirmBtnCssClass="btn btn-danger"
        cancelBtnText="No"
        cancelBtnCssClass="btn btn-success"
        show={countryIndicationConfirmation}
        showCancel={true}
        showCloseButton={true}
        onClose={countryIndicationClose}
        onCancel={countryIndicationClose}
        onConfirm={onConfirmCountryIndication}
      >
        If you select YES, you should not take this test. Are you sure you want to select YES?
      </AlertModal>
    </>
  );
};

export default TakeBiometricMovements;
